import React, { useState } from "react";
import {
  Box, Button, Grid, Snackbar, Alert, TextField, MenuItem, FormControl
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { Formik, Field, Form, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { AxiosResponse } from "axios";
import { useDispatch } from "react-redux";
import { CmsMasterData } from "types/models/cmsMaster/CmsMasterApp";
import { updateCms } from "redux/actions/cmsMaster";
import RichTextEditor from "@crema/utility/RichTextEditor";

interface CmsProps {
  Cms?: CmsMasterData;
}

const CmsUpdate: React.FC<CmsProps> = ({ Cms }) => {
  const navigate = useNavigate();
  const [fileError, setFileError] = useState(false);
  const dispatch = useDispatch();

  const validationSchema = Yup.object({
    PageTitle: Yup.string().required("Page Title is required"),
    MetaTitle: Yup.string().required("Meta Title is required"),
    MetaKeyword: Yup.string().required("Meta Keyword is required"),
    MetaDescription: Yup.string(),
    Description1: Yup.string(),
    Description2: Yup.string(),
    Description3: Yup.string(),
    Description4: Yup.string(),
    HeaderName1: Yup.string(),
    HeaderName2: Yup.string(),
    HeaderName3: Yup.string(),
    HeaderName4: Yup.string(),
    status: Yup.string(),
  });

  const handleSubmit = async (values: any) => {
    

    const dataPayload = {
      PageID: Cms?.PageID?.toString() || "0",
      PageTitle: values.PageTitle,
      MetaTitle: values.MetaTitle,
      MetaKeyword: values.MetaKeyword,
      MetaDesc: values.MetaDescription,
      Description1: values.Description1,
      Description2: values.Description2,
      Description3: values.Description3,
      Description4: values.Description4,
      HeaderName1: values.HeaderName1,
      HeaderName2: values.HeaderName2,
      HeaderName3: values.HeaderName3,
      HeaderName4: values.HeaderName4,
      status: values.status,
      IsActive: values.status === "Active" ? 1 : 0,
    };
    
    

    try {
      const pageId = Cms?.PageID?.toString() || "0";
      const response = await dispatch(updateCms(pageId, dataPayload)) as unknown as AxiosResponse<any>;

      if (response.status === 200) {
        navigate(`/pages`);
      }
    } catch (error) {
      console.error("Error updating Cms:", error);
    }
  };

  return (
    <Box sx={{ height: "100%", width: "100%", m: 8 }}>
      <Grid
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          height: "100%",
          margin: 2,
        }}
      >
        <Formik
         initialValues={{
          PageID: Cms?.PageID || "",
          PageTitle: Cms?.PageTitle || "",
          MetaTitle: Cms?.MetaTitle || "",
          MetaKeyword: Cms?.MetaKeyword || "",
          MetaDescription: Cms?.MetaDesc || "",
          Description1: Cms?.Description1 || "",
          Description2: Cms?.Description2 || "",
          Description3: Cms?.Description3 || "",
          Description4: Cms?.Description4 || "",
          HeaderName1: Cms?.HeaderName1 || "",
          HeaderName2: Cms?.HeaderName2 || "",
          HeaderName3: Cms?.HeaderName3 || "",
          HeaderName4: Cms?.HeaderName4 || "",
          status: Cms?.IsActive === 1 ? "Active" : "InActive", 
        }}
        
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ values, errors, touched, isSubmitting, setFieldValue }) => (
            <Form>
              <Grid container spacing={3} mb={3}>
                <Grid item xs={6}>
                  <Field
                    as={TextField}
                    fullWidth
                    label="Page Title"
                    name="PageTitle"
                    variant="outlined"
                    error={touched.PageTitle && !!errors.PageTitle}
                    helperText={<ErrorMessage name="PageTitle" />}
                  />
                </Grid>
                <Grid item xs={6}>
                  <Field
                    as={TextField}
                    fullWidth
                    label="Meta Title"
                    name="MetaTitle"
                    variant="outlined"
                    error={touched.MetaTitle && !!errors.MetaTitle}
                    helperText={<ErrorMessage name="MetaTitle" />}
                  />
                </Grid>
              </Grid>

              <Grid container spacing={3} mb={3}>
                <Grid item xs={6}>
                  <Field
                    as={TextField}
                    fullWidth
                    label="Meta Keyword"
                    name="MetaKeyword"
                    variant="outlined"
                    error={touched.MetaKeyword && !!errors.MetaKeyword}
                    helperText={<ErrorMessage name="MetaKeyword" />}
                  />
                </Grid>

                <Grid item xs={6}>
                  <Field
                    as={TextField}
                    fullWidth
                    label="Status"
                    name="status"
                    select
                    variant="outlined"
                    error={touched.status && !!errors.status}
                    helperText={<ErrorMessage name="status" />}
                  >
                    <MenuItem value="Active">Active</MenuItem>
                    <MenuItem value="InActive">Inactive</MenuItem>
                  </Field>
                </Grid>
              </Grid>

              <Grid container spacing={3} mb={3}>
                <Grid item xs={12}>
                  <Field
                    as={TextField}
                    fullWidth
                    multiline
                    minRows={3}
                    label="Meta Description"
                    name="MetaDescription"
                    variant="outlined"
                    error={touched.MetaDescription && !!errors.MetaDescription}
                    helperText={<ErrorMessage name="MetaDescription" />}
                  />
                </Grid>
              </Grid>

             
              <Grid container spacing={3} mb={3}>
                {["HeaderName1", "HeaderName2", "HeaderName3", "HeaderName4"].map((fieldName, idx) => (
                  <Grid item xs={6} key={fieldName}>
                    <FormControl fullWidth>
                      <label>{`Header Name ${idx + 1}`}</label>
                      <div style={{ height: 150, overflow: "auto", border: "1px solid #ccc", borderRadius: 4 }}>
                        <RichTextEditor
                          disabled={false}
                          value={values[fieldName]}
                          onChange={(value) => setFieldValue(fieldName, value)}
                        />
                      </div>
                    </FormControl>
                  </Grid>
                ))}
              </Grid>

              
              <Grid container spacing={3} mb={3}>
                {["Description1", "Description2", "Description3", "Description4"].map((fieldName, idx) => (
                  <Grid item xs={6} key={fieldName}>
                    <FormControl fullWidth>
                      <label>{`Description ${idx + 1}`}</label>
                      <div style={{ height: 150, overflow: "auto", border: "1px solid #ccc", borderRadius: 4 }}>
                        <RichTextEditor
                          disabled={false}
                          value={values[fieldName]}
                          onChange={(value) => setFieldValue(fieldName, value)}
                        />
                      </div>
                    </FormControl>
                  </Grid>
                ))}
              </Grid>

             
              <Box
                sx={{
                  position: "fixed",
                  bottom: 0,
                  left: 0,
                  width: "100%",
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: 2,
                  padding: 2,
                  zIndex: 1000,
                  backgroundColor: "white",
                  boxShadow: "0 -2px 10px rgba(0, 0, 0, 0.1)",
                }}
              >
                <Button
                  variant="outlined"
                  color="secondary"
                  type="button"
                  onClick={() => navigate("/pages")}
                  sx={{ textTransform: "none" }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={isSubmitting}
                  sx={{ textTransform: "none" }}
                >
                  {isSubmitting ? "Submitting..." : "Update CMS"}
                </Button>
              </Box>
            </Form>
          )}
        </Formik>
      </Grid>

      <Snackbar
        open={fileError}
        autoHideDuration={6000}
        onClose={() => setFileError(false)}
      >
        <Alert onClose={() => setFileError(false)} severity="error" sx={{ width: "100%" }}>
          {fileError ? "You can only upload one image." : "Cms update successfully!"}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CmsUpdate;
